/**
 * Gate Runtime
 *
 * DOM adapter for the shared headless gate manager. Components declare intent
 * with data-gate-* attributes; this file wires those declarations to runtime
 * state, lead capture submission, and pending action replay.
 */

import { DreamRealityRuntime } from './dream-reality-runtime.js';
import { ConditionEvaluator } from '../core/condition-evaluator.js';
import { createGateManager, type GateActionMode, type GateConfig, type GateManager } from '../core/gate-manager.js';

type GateFeedbackReason = 'download_unavailable' | 'submission_failed';

interface DlrConfig {
  gates?: GateConfig[];
  stateTypes?: Record<string, any>;
  sectionData?: Record<string, Record<string, any>>;
}

interface GateFeedbackDetail {
  gateId?: string;
  actionId?: string;
  reason: GateFeedbackReason;
  message: string;
  source: HTMLElement;
}

class GateDomRuntime {
  private manager: GateManager;
  private runtime: DreamRealityRuntime;
  private conditionEvaluator: ConditionEvaluator;
  private sectionData: Record<string, Record<string, any>>;

  constructor(config: DlrConfig) {
    const stateTypes = config.stateTypes || {};
    const gates = (config.gates || [])
      .map((gate) => this.withGateStateDefaults(gate, stateTypes))
      .filter((gate) => this.isValidGateStateMachine(gate, stateTypes));
    this.sectionData = config.sectionData || {};

    this.runtime = new DreamRealityRuntime({
      stateTypes,
      context: 'gate',
    });

    this.conditionEvaluator = new ConditionEvaluator(this.runtime);
    this.manager = createGateManager({
      gates,
      storage: window.localStorage,
      onStateChange: (_gateId, state, statePath) => {
        this.runtime.setState(statePath, state, false);
        this.evaluate();
      },
    });

    for (const snapshot of this.manager.getSnapshots()) {
      this.runtime.setState(snapshot.statePath, snapshot.state, false);
    }

    this.init();
  }

  private init(): void {
    this.evaluate();
    this.bindTriggers();
    this.bindForms();
    this.bindClosers();

    (window as any).dlrGate = {
      open: (actionId?: string, gateId?: string) => this.manager.open(actionId, gateId),
      close: (gateId?: string) => this.manager.close(gateId),
      unlock: (gateId?: string) => this.manager.unlock(gateId),
      isUnlocked: (gateId?: string) => this.manager.isUnlocked(gateId),
      performAction: (actionId: string, gateId?: string) => this.manager.performAction(actionId, gateId),
    };
  }

  private bindTriggers(): void {
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement | null;
      const trigger = target?.closest('[data-gate-trigger]') as HTMLElement | null;
      if (!trigger) return;

      const gateId = trigger.getAttribute('data-gate-trigger') || undefined;
      const actionId = trigger.getAttribute('data-gate-action') || undefined;
      const mode = (trigger.getAttribute('data-gate-mode') || this.getConfiguredActionMode(gateId, actionId)) as GateActionMode | undefined;

      if (this.manager.isUnlocked(gateId)) {
        event.preventDefault();
        event.stopPropagation();
        if (mode === 'download') this.performDomAction(trigger, mode);
        else this.manager.performAction(actionId || '', gateId);
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      this.manager.open(actionId, gateId, {
        mode,
        perform: () => this.performDomAction(trigger, mode),
      });
    }, true);
  }

  private bindForms(): void {
    document.addEventListener('submit', async (event) => {
      const formEl = event.target instanceof HTMLFormElement
        ? event.target
        : null;
      if (!formEl?.matches('form[data-gate-form]')) return;

      event.preventDefault();
      event.stopPropagation();
      const gateId = formEl.getAttribute('data-gate-form') || undefined;

      if (!formEl.reportValidity()) return;

      const submitButton = formEl.querySelector('[type="submit"]') as HTMLButtonElement | null;
      const previousText = submitButton?.textContent || '';
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = submitButton.getAttribute('data-loading-text') || 'Submitting...';
      }

      try {
        await this.manager.submitLead(gateId, this.getFormPayload(formEl));
        this.manager.unlock(gateId);
      } catch (error) {
        console.error('[DLR Gate] Lead submission failed:', error);
        this.manager.fail(gateId);
        formEl.setAttribute('data-gate-error', 'true');
        this.showFeedback(formEl, 'submission_failed', 'We could not submit your details. Please try again.');
      } finally {
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = previousText;
        }
      }
    }, true);
  }

  private bindClosers(): void {
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement | null;
      const closer = target?.closest('[data-gate-close]') as HTMLElement | null;
      if (!closer) return;
      event.preventDefault();
      this.manager.close(closer.getAttribute('data-gate-close') || undefined);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.manager.close();
      }
    });
  }

  private evaluate(): void {
    this.conditionEvaluator.evaluateAll(this.sectionData);

    document.querySelectorAll('[data-gate-content]').forEach((element) => {
      const gateId = element.getAttribute('data-gate-content') || undefined;
      (element as HTMLElement).style.display = this.manager.isUnlocked(gateId) ? '' : 'none';
    });
  }

  private performDomAction(trigger: HTMLElement, mode?: GateActionMode): void {
    if (mode === 'download') {
      const href = this.getDownloadHref(trigger);
      if (href) {
        window.location.href = href;
      } else {
        trigger.hidden = true;
        trigger.setAttribute('aria-hidden', 'true');
        trigger.style.display = 'none';
        this.showFeedback(trigger, 'download_unavailable', 'Thank you. Your details were received, but the brochure is not available yet. Our advisory team will contact you shortly.');
      }
    }
  }

  private getDownloadHref(trigger: HTMLElement): string | null {
    const link = trigger.closest('a') as HTMLAnchorElement | null;
    const rawHref = link?.getAttribute('href') || trigger.getAttribute('href') || '';
    if (!rawHref || rawHref === '#') return null;
    return link?.href || rawHref;
  }

  private showFeedback(source: HTMLElement, reason: GateFeedbackReason, message: string): void {
    const customMessage = source.getAttribute('data-gate-error-message') || message;
    const detail = this.getFeedbackDetail(source, reason, customMessage);

    source.dispatchEvent(new CustomEvent<GateFeedbackDetail>('dlr:gate-feedback', { bubbles: true, detail }));
    window.dispatchEvent(new CustomEvent<GateFeedbackDetail>('dlr:gate-feedback', { detail }));

    const slot = this.findFeedbackSlot(source, detail);
    const feedback = slot || this.ensureFallbackFeedbackElement();
    const messageEl = feedback.querySelector('[data-gate-feedback-message]');
    if (messageEl) messageEl.textContent = customMessage;
    feedback.hidden = false;
    feedback.removeAttribute('aria-hidden');
    if (!slot) feedback.querySelector<HTMLButtonElement>('[data-gate-feedback-close]')?.focus();
  }

  private getFeedbackDetail(source: HTMLElement, reason: GateFeedbackReason, message: string): GateFeedbackDetail {
    const gateId = source.getAttribute('data-gate-trigger') || source.getAttribute('data-gate-form') || undefined;
    const actionId = source.getAttribute('data-gate-action') || undefined;
    return { gateId, actionId, reason, message, source };
  }

  private findFeedbackSlot(source: HTMLElement, detail: GateFeedbackDetail): HTMLElement | null {
    const section = source.closest('[data-dr-section]');
    const candidates = Array.from(document.querySelectorAll<HTMLElement>('[data-gate-feedback]:not([data-gate-feedback-fallback])'));
    const scopedCandidates = section
      ? candidates.filter((candidate) => section.contains(candidate))
      : [];

    return [...scopedCandidates, ...candidates]
      .filter((candidate, index, all) => all.indexOf(candidate) === index)
      .filter((candidate) => this.feedbackSlotMatches(candidate, detail))
      .sort((left, right) => this.feedbackSlotScore(right, detail) - this.feedbackSlotScore(left, detail))[0] || null;
  }

  private feedbackSlotMatches(slot: HTMLElement, detail: GateFeedbackDetail): boolean {
    const gateAttr = slot.getAttribute('data-gate-feedback');
    const actionAttr = slot.getAttribute('data-gate-feedback-action');
    const reasonAttr = slot.getAttribute('data-gate-feedback-reason');

    if (gateAttr && gateAttr !== detail.gateId) return false;
    if (actionAttr && actionAttr !== detail.actionId) return false;
    if (reasonAttr && reasonAttr !== detail.reason) return false;
    return true;
  }

  private feedbackSlotScore(slot: HTMLElement, detail: GateFeedbackDetail): number {
    let score = 0;
    if (slot.getAttribute('data-gate-feedback') === detail.gateId) score += 1;
    if (slot.getAttribute('data-gate-feedback-action') === detail.actionId) score += 2;
    if (slot.getAttribute('data-gate-feedback-reason') === detail.reason) score += 4;
    return score;
  }

  private ensureFallbackFeedbackElement(): HTMLElement {
    const existing = document.querySelector<HTMLElement>('[data-gate-feedback-fallback]');
    if (existing) return existing;

    const feedback = document.createElement('div');
    feedback.setAttribute('data-gate-feedback', '');
    feedback.setAttribute('data-gate-feedback-fallback', '');
    feedback.setAttribute('role', 'alertdialog');
    feedback.setAttribute('aria-modal', 'true');
    feedback.hidden = true;
    feedback.innerHTML = `
      <div data-gate-feedback-backdrop style="position:fixed;inset:0;z-index:9998;background:rgba(0,0,0,.5);"></div>
      <div style="position:fixed;left:50%;top:50%;z-index:9999;max-width:420px;width:calc(100% - 32px);transform:translate(-50%,-50%);background:#fff;color:#111;padding:24px;border:1px solid rgba(0,0,0,.12);box-shadow:0 24px 80px rgba(0,0,0,.28);">
        <p data-gate-feedback-message style="margin:0 0 18px;font:14px/1.5 system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;"></p>
        <button data-gate-feedback-close type="button" style="min-height:44px;border:1px solid #111;background:#111;color:#fff;padding:0 18px;font:11px/1 system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;text-transform:uppercase;letter-spacing:.16em;cursor:pointer;">Close</button>
      </div>
    `;

    const close = () => {
      feedback.hidden = true;
      feedback.setAttribute('aria-hidden', 'true');
    };
    feedback.querySelector('[data-gate-feedback-backdrop]')?.addEventListener('click', close);
    feedback.querySelector('[data-gate-feedback-close]')?.addEventListener('click', close);
    document.body.appendChild(feedback);
    return feedback;
  }

  private getFormPayload(form: HTMLFormElement): Record<string, unknown> {
    const payload: Record<string, unknown> = {};
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      payload[key] = value;
    });
    payload.source_url = window.location.href;
    const formType = form.getAttribute('data-gate-form-type');
    if (!formType) {
      console.warn('[DLR Gate] Gate form is missing data-gate-form-type; template validation should catch this before deployment.');
    }
    payload.form_type = formType || form.getAttribute('data-gate-form') || 'unknown';
    payload.__submissionEndpoint = form.dataset.endpoint || '';
    payload.__siteToken = form.dataset.token || '';
    payload.__supabaseAnonKey = form.dataset.apikey || '';
    return payload;
  }

  private getConfiguredActionMode(gateId?: string, actionId?: string): GateActionMode | undefined {
    const action = this.manager.getGate(gateId)?.actions?.find((item) => item.id === actionId);
    return action?.mode;
  }

  private withGateStateDefaults(gate: GateConfig, stateTypes: Record<string, any>): GateConfig {
    const gateId = gate.id || 'lead-capture';
    const statePath = gate.statePath;
    const values = Array.isArray(stateTypes[statePath]?.values)
      ? stateTypes[statePath].values.map(String)
      : [];
    const defaultState = gate.defaultState || stateTypes[statePath]?.default || values[0] || '';
    const successState = gate.successState || defaultState;
    const formState = gate.formState || defaultState;

    return {
      ...gate,
      id: gateId,
      statePath,
      defaultState,
      formState,
      successState,
      persistStates: gate.persistStates?.length ? gate.persistStates : [successState],
    };
  }

  private isValidGateStateMachine(gate: GateConfig, stateTypes: Record<string, any>): boolean {
    const gateId = gate.id || 'lead-capture';
    const statePath = gate.statePath;
    const stateType = stateTypes[statePath];

    if (!stateType || stateType.type !== 'enum' || !Array.isArray(stateType.values)) {
      console.warn(`[DLR Gate] Gate "${gateId}" skipped: statePath "${statePath}" must reference an enum in manifest.stateTypes.`);
      return false;
    }

    const allowedStates = new Set(stateType.values.map(String));
    const roleStates = [
      gate.defaultState,
      gate.formState,
      gate.successState,
      gate.failureState,
      gate.expiredState,
      ...(gate.persistStates || []),
    ].filter((state): state is string => typeof state === 'string' && state.length > 0);

    const invalidState = roleStates.find((state) => !allowedStates.has(state));
    if (invalidState) {
      console.warn(`[DLR Gate] Gate "${gateId}" skipped: state "${invalidState}" is not declared in stateTypes["${statePath}"].values.`);
      return false;
    }

    return true;
  }
}

function initializeGateRuntime(): void {
  const config = (window as any).__DLR as DlrConfig | undefined;
  if (!config?.gates?.length) return;
  new GateDomRuntime(config);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeGateRuntime);
} else {
  initializeGateRuntime();
}
